import { Send, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { useReportPdfAction } from './ReportPdf';
import type { Report } from './types';

/**
 * The page's foot: «Elimina bozza», «Invia per approvazione» and the note's PDF, each only when
 * `can` allows it. «Invia» stays visible but off while something blocks it, with the reason under it.
 */
export function ReportBar({
  report,
  busy,
  onSubmit,
  onDiscard,
}: {
  readonly report: Report;
  readonly busy: boolean;
  readonly onSubmit: () => void;
  readonly onDiscard: () => void;
}) {
  const { t } = useTranslation('expenses');
  // Round 1 keeps this confirmation: the server soft-deletes a draft but offers no restore, so an
  // undo toast cannot work yet. The undoable delete (U9, U10) is a round-2 item: «Undo for a
  // deleted draft report», which needs a restore endpoint.
  const [confirming, setConfirming] = useState(false);
  const pdf = useReportPdfAction(report);
  const can = report.can ?? {};
  const blockers = report.submitBlockers ?? [];
  const showSubmit = can.submit === true || (can.editExpenses === true && blockers.length > 0);
  const reasons = can.submit === true ? [] : [...new Set(blockers.map((blocker) => blocker.code))];
  if (can.delete !== true && !showSubmit && pdf === null) return null;

  return (
    <div className="border-border flex flex-col items-end gap-2 border-t pt-4">
      <div className="flex flex-wrap justify-end gap-2">
        {pdf?.controls}
        {can.delete === true && !confirming ? (
          <Button
            type="button"
            variant="outline"
            disabled={busy}
            onClick={() => setConfirming(true)}
          >
            <Trash2 />
            {t('reports.page.delete')}
          </Button>
        ) : null}
        {confirming ? (
          <span
            role="alertdialog"
            aria-label={t('reports.page.delete')}
            className="flex items-center gap-2 text-sm"
          >
            {t('reports.page.deleteAsk')}
            <Button
              type="button"
              variant="destructive"
              size="sm"
              disabled={busy}
              onClick={onDiscard}
            >
              {t('reports.page.deleteConfirm')}
            </Button>
            <Button type="button" variant="ghost" size="sm" onClick={() => setConfirming(false)}>
              {t('reports.new.cancel')}
            </Button>
          </span>
        ) : null}
        {showSubmit ? (
          <Button type="button" disabled={can.submit !== true || busy} onClick={onSubmit}>
            <Send />
            {t('reports.page.submit')}
          </Button>
        ) : null}
      </div>
      {reasons.map((code) => (
        <span key={code} className="text-muted-foreground text-sm">
          {t(`reports.blockers.${code}`)}
        </span>
      ))}
      {pdf?.messages}
    </div>
  );
}
